import {
  OverviewPeriod,
  TypeAttribute,
  TypeCrashSortKey,
  TypeCrashStatus,
  TypeMetricKey,
} from '@trace/types';

import { TypeOnboardingSetupOption } from '../components/Onboarding/Onboarding';
import { TypeDuration } from '../constants/TimeRange';
import getAppIdFromUrl from './getAppIdFromUrl';

declare global {
  interface Window {
    analytics?: {
      load: (id: string) => void;
      page: () => void;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      track: (event: string, data: any) => void;
    };
  }
}

export interface TypeTraceEventData {
  'AppSelector.Visit': Record<string, unknown>;
  'AppSelector.ExpandVP': Record<string, unknown>;
  'AppSelector.GotoTrace': { appSlug: string };
  'BuildPerformanceOverview.Visit': Record<string, unknown>;
  'BuildWorkflow.Visit': Record<string, unknown>;
  'BuildWorkflows.Visit': Record<string, unknown>;
  'Filters.Added': { attribute: TypeAttribute; value: string };
  'Filters.Cleared': Record<string, unknown>;
  'Filters.Removed': { attribute: TypeAttribute; value: string };
  'IssueDetails.Page': { direction: 'prev' | 'next' };
  'IssueDetails.Exception.Expand': Record<string, unknown>;
  'IssueDetails.Threads.Expand': Record<string, unknown>;
  'IssueDetails.Visit': Record<string, unknown>;
  'IssueDetails.ViewDetails': Record<string, unknown>;
  'IssueDetails.ViewSession': Record<string, unknown>;
  'IssueList.Visit': Record<string, unknown>;
  'IssuesList.Filter': { status: TypeCrashStatus | 'all' };
  'IssuesList.Sort': { field: TypeCrashSortKey; direction: string };
  'Metric.GroupBy': { attribute: TypeAttribute; metric: TypeMetricKey };
  'Metric.Interaction': { attribute: TypeAttribute; metric: TypeMetricKey };
  'Onboarding.Setup.Complete': Record<string, unknown>;
  'Onboarding.Setup.Copy': { content: string };
  'Onboarding.Setup.SDKDetected': Record<string, unknown>;
  'Onboarding.Setup.Visit': { guide: TypeOnboardingSetupOption };
  'Onboarding.Welcome.AcceptedToS': Record<string, unknown>;
  'Onboarding.Welcome.Complete': Record<string, unknown>;
  'Onboarding.Welcome.Visit': Record<string, unknown>;
  'Onboarding.Welcome.ViewedToS': Record<string, unknown>;
  'Overview.Visit': {
    period: OverviewPeriod;
  };
  'Overview.WidgetDetails': {
    widget: 'crashes' | 'performance' | 'adoption' | 'engagement';
  };
  'PerformanceDetail.Breakdown.Interaction': { attribute: TypeAttribute; metric: TypeMetricKey };
  'PerformanceDetail.BreakdownFilter.Added': {
    attribute: TypeAttribute;
    metric: TypeMetricKey;
    value: string;
  };
  'PerformanceDetail.BreakdownFilter.Removed': {
    attribute: TypeAttribute;
    metric: TypeMetricKey;
    value: string;
  };
  'PerformanceDetail.Visit': { metric: TypeMetricKey };
  'PerformanceOverview.Visit': Record<string, unknown>;
  'SessionExplorer.Events.Interaction': Record<string, unknown>;
  'SessionExplorer.Metric.Interaction': { metric: TypeMetricKey };
  'SessionExplorer.Navigate.Next': Record<string, unknown>;
  'SessionExplorer.Navigate.Previous': Record<string, unknown>;
  'SessionExplorer.Trace.Click': Record<string, unknown>;
  'SessionExplorer.Trace.Interaction': Record<string, unknown>;
  'SessionExplorer.Visit': Record<string, unknown>;
  'Setup.DownloadConfig': { platform: 'android' | 'ios' };
  'TimeRange.Custom': { dateFrom: string; dateTo: string };
  'TimeRange.Predefined': { duration: TypeDuration };
}

export type TypeTraceEvent = keyof TypeTraceEventData;

export const trackLoad = (): void => {
  if (window.analytics && window.analytics.load) {
    window.analytics.load('sFGzDYl6EseyyNwmrlHSJ1cBXtDXWTDj');
  }
};

export const trackPage = (): void => {
  if (window.analytics && window.analytics.page) {
    window.analytics.page();
  }
};

export const trackEvent = <E extends TypeTraceEvent>(
  event: E,
  data: TypeTraceEventData[E],
): void => {
  const appId = getAppIdFromUrl();
  const dataFinal = { ...data, ...(appId ? { appSlug: appId } : undefined) };

  if (window.analytics && window.analytics.track) {
    window.analytics.track(`Trace.${event}`, dataFinal);
  } else if (process.env.NODE_ENV === 'development') {
    /**
     * We don't want to actually track the event in dev mode
     * but it is useful to see in development what events would
     * be tracked and with what data.
     */
    // eslint-disable-next-line no-console
    console.info(`Tracking: ${event}`, dataFinal);
  }
};
